import { useParams } from 'react-router-dom';
import { Paper, Stack, Typography } from '@mui/material';
import { ReactFlowProvider } from '@xyflow/react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveIcon from '@mui/icons-material/Save';
import MDBox from 'components/MDBox';
import { DnDProvider } from 'pages/assistants/builder/components/dnDContext';
import { FlowCanvas } from 'pages/assistants/builder/components/flowCanvas';
import AssistantDetailsDrawer from 'pages/assistants/components/assistantDetailsDrawer';
import { useGetAssistantQuery, useUpdateAssistantFlowMutation } from 'services/assistantApi';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { useNotification } from 'hook/useNotification';
import { SideBar } from 'pages/assistants/builder/components/sidebar/sideBar';
import MDButton from 'components/MDButton';
import { FlowProvider, useFlow } from 'pages/assistants/builder/components/flowContext';
import { isAuthorized } from 'utils/userUtils';
import { RoleEnum } from 'pages/authentication/sign-in';
import { useAuth } from 'hook/useAuth';

function AssistantBuilderContent() {
  const { assistantId } = useParams();
  const { addNotification } = useNotification();
  const { saveFlow } = useFlow();
  const { formatMessage: msg } = useIntl();
  const [assistantDetailDrawerOpen, setAssistantDetailDrawerOpen] = useState(false);

  const { currentUser } = useAuth();
  const { user } = currentUser;

  const [updateAssistantFlow] = useUpdateAssistantFlowMutation();

  const hasAdminRole = isAuthorized(user, RoleEnum.ADMIN);

  const {
    data: assistant,
    error: assistantError,
    isLoading: assistantLoading
  } = useGetAssistantQuery({
    assistantId: assistantId,
    isPublic: false
  });

  const handleSaveFlow = async () => {
    if (saveFlow) {
      try {
        const flowData = saveFlow();
        await updateAssistantFlow({
          assistantId: assistant.id,
          flow: flowData
        }).unwrap();

        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.assistant.flow.save.success' })
        });
      } catch {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.assistant.flow.save.error' })
        });
      }
    }
  };

  if (assistantLoading) {
    return (
      <IndeterminateLoading
        sx={{ width: '100%', color: '#125688' }}
        message={msg({ id: 'loading.configuration' })}
      />
    );
  }

  if (assistantError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.assistant.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  return (
    <>
      <MDBox id="assistantsContainer" sx={{ display: 'flex', flex: 1 }}>
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            borderRadius: '10px',
            overflow: 'hidden',
            display: 'flex'
          }}
        >
          <SideBar />
          <MDBox sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <MDBox id="topBar" sx={{ display: 'flex', p: 1 }}>
              <MDBox sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" ml={2}>
                  {assistant?.label ?? ''}
                </Typography>
              </MDBox>
              <Stack id="buttonBar" direction="row" spacing={2}>
                <MDButton
                  id="saveBtn"
                  color="infoBlue"
                  startIcon={<SaveIcon />}
                  autoFocus
                  onClick={() => handleSaveFlow()}
                >
                  {msg({ id: 'action.save' })}
                </MDButton>
                {hasAdminRole && (
                  <MDButton
                    id="editBtn"
                    color="primary"
                    variant="gradient"
                    startIcon={<EditNoteIcon />}
                    autoFocus
                    onClick={() => setAssistantDetailDrawerOpen(true)}
                  >
                    {msg({ id: 'action.edit' })}
                  </MDButton>
                )}
              </Stack>
            </MDBox>
            <MDBox
              flex={1}
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh' }}
            >
              <FlowCanvas flow={assistant?.flow} />
            </MDBox>
          </MDBox>
        </Paper>
      </MDBox>
      <AssistantDetailsDrawer
        drawerOpen={assistantDetailDrawerOpen}
        setDrawerOpen={setAssistantDetailDrawerOpen}
        assistant={assistant}
      />
    </>
  );
}

export function AssistantBuilder() {
  return (
    <DnDProvider>
      <FlowProvider>
        <ReactFlowProvider>
          <AssistantBuilderContent />
        </ReactFlowProvider>
      </FlowProvider>
    </DnDProvider>
  );
}
